import { InformationIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";

interface InfoNoteProps {
  title?: string;
  message: React.ReactNode;
  /** `highlight` is the blue titled note, `muted` is a grey box carrying the message alone. */
  variant?: "highlight" | "muted";
  className?: string;
  /** Screen-reader name, so several notes on one panel stay distinguishable. */
  ariaLabel?: string;
  "data-testid"?: string;
}

const VARIANT_CLASSNAME = {
  highlight: {
    container: "",
    iconWrapper:
      "flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-blue-50",
    icon: "fill-royal-blue",
    heading: "text-royal-blue",
    message: "text-[10px] text-txtBody"
  },
  muted: {
    container: "rounded-xl bg-white-smoke p-3",
    iconWrapper: "shrink-0",
    icon: "fill-dim-gray",
    heading: "text-sm text-txtBody mb-1",
    message: "text-xs text-dim-gray"
  }
};

const InfoNote = ({
  title,
  message,
  variant = "highlight",
  className,
  ariaLabel = "information",
  ...rest
}: InfoNoteProps) => {
  const variantClassName = VARIANT_CLASSNAME[variant];
  // Only the highlight note is headed, so the fallback heading applies there alone.
  const headingText =
    variant === "highlight" ? (title ?? "Information") : title;

  return (
    <div
      className={cn(
        "flex items-start gap-2",
        variantClassName.container,
        className
      )}
      role="note"
      aria-label={ariaLabel}
      {...rest}
    >
      <div className={variantClassName.iconWrapper}>
        <InformationIcon
          className={cn("size-4", variantClassName.icon)}
          data-testid="information-icon"
        />
      </div>

      <div className="flex flex-col">
        {headingText && (
          <div
            className={cn(
              "leading-6 font-semibold text-xs",
              variantClassName.heading
            )}
          >
            {headingText}
          </div>
        )}
        <span className={cn("whitespace-pre-line", variantClassName.message)}>
          {message}
        </span>
      </div>
    </div>
  );
};

export default InfoNote;
